import { observer } from "mobx-react";
import { Checkbox, Select, Alert, Form } from "antd";

interface GeneralSettingsStepProps {
  data: {
    enableNodeAggregation: boolean;
    nodeAggregationCriteria: string[];
    enableStopJobAggregation: boolean;
    stopJobAggregationCriteria: string[];
    stopTimeComputationMode: string;
    jobTimeComputationMode: string;
  };
  modules: {
    routeView: boolean;
    dispatch: boolean;
    tactical: boolean;
  };
  onChange: (data: any) => void;
}

const GeneralSettingsStep = observer(({ data, modules, onChange }: GeneralSettingsStepProps) => {
  const showSettings = modules.routeView || modules.dispatch || modules.tactical;

  if (!showSettings) {
    return (
      <div className="p-4">
        <Alert message="No Configuration Required" description="General settings are only applicable when Route View or Dispatch View/Mobile is selected." type="info" showIcon />
      </div>
    );
  }

  const stopJobAggregationCriteriaOptions = [
    { label: "Customer Account Ref", value: "customerAccountRef" },
    { label: "Known Location Ref", value: "knownLocationRef" },
    { label: "House Number", value: "houseNumber" },
    { label: "House Name", value: "houseName" },
    { label: "Lat/Long", value: "coordinates" },
  ];

  const handleChange = (field: string, value: any) => {
    const updates: any = { [field]: value };

    // Auto-enable latLong if node aggregation is disabled
    if (field === "enableNodeAggregation" && !value && data.enableStopJobAggregation) {
      updates.stopJobAggregationCriteria = [...data.stopJobAggregationCriteria, "coordinates"].filter((v, i, a) => a.indexOf(v) === i);
    }

    // Disable stop job aggregation criteria if stop job aggregation is disabled
    if (field === "enableStopJobAggregation" && !value) {
      updates.stopJobAggregationCriteria = [];
    }

    onChange({ ...data, ...updates });
  };

  return (
    <div className="p-4">
      <Alert message="General Settings for Job Processing" description="Configure how jobs and stops are processed for Route View and Dispatch View." type="info" showIcon className="mb-6" />

      <Form layout="vertical">
        {/* Node Aggregation */}
        <Form.Item label={<div className="font-bold">Node Aggregation</div>} className="mb-4">
          <Checkbox checked={data.enableNodeAggregation} onChange={(e) => handleChange("enableNodeAggregation", e.target.checked)}>
            Aggregate stops into nodes?
          </Checkbox>
          {data.enableNodeAggregation && (
            <div className="ml-6 mt-2 text-sm text-gray-600">
              Node aggregation filter criteria: <strong>Lat/Long</strong>
            </div>
          )}
        </Form.Item>

        {/* Stop Job Aggregation */}
        <Form.Item label={<div className="font-bold">Stop Job Aggregation</div>} className="mb-4">
          <Checkbox checked={data.enableStopJobAggregation} onChange={(e) => handleChange("enableStopJobAggregation", e.target.checked)}>
            Aggregate jobs into stop?
          </Checkbox>
        </Form.Item>

        {data.enableStopJobAggregation && (
          <Form.Item label="Select Aggregation Criteria" className="mb-4">
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Select one or more criteria for job aggregation"
              value={data.stopJobAggregationCriteria}
              onChange={(value) => handleChange("stopJobAggregationCriteria", value)}
              options={stopJobAggregationCriteriaOptions.map((option) => ({
                ...option,
                disabled: option.value === "coordinates" && !data.enableNodeAggregation,
              }))}
            />
            {!data.enableNodeAggregation && <div className="text-xs text-gray-500 mt-1">Note: Latitude & Longitude is enabled and read-only when Node Aggregation is disabled</div>}
          </Form.Item>
        )}

        {/* Stop Time Computation Mode */}
        <Form.Item label={<div className="font-bold">Stop Time Computation Mode</div>} className="mb-4">
          <Select
            style={{ width: "100%" }}
            value={data.stopTimeComputationMode}
            onChange={(value) => handleChange("stopTimeComputationMode", value)}
            options={[
              { label: "Job", value: "job" },
              { label: "Product Skill", value: "productSkill" },
            ]}
          />
        </Form.Item>

        {/* Job Time Computation Mode */}
        <Form.Item label={<div className="font-bold">Job Time Computation Mode</div>} className="mb-4">
          <Select
            style={{ width: "100%" }}
            value={data.jobTimeComputationMode}
            onChange={(value) => handleChange("jobTimeComputationMode", value)}
            options={[
              { label: "Job Total", value: "jobTotal" },
              { label: "Job Total Average", value: "jobTotalAverage" },
              { label: "Highest Job Time", value: "highestJobTime" },
              { label: "Lowest Job Time", value: "lowestJobTime" },
            ]}
          />
        </Form.Item>
      </Form>
    </div>
  );
});

export default GeneralSettingsStep;
